import React, { useCallback, useState } from 'react'
import { Formik, Form } from 'formik'
import { getOrDefault } from 'libs/PagingParser'
import { PlanId } from 'commons/plan'
import momentTimezone from 'moment-timezone'
import { first, compact } from 'lodash'
import {
  view,
  lensPath,
  propEq,
  filter,
  isNil,
  prop,
  compose,
  not,
  isEmpty,
} from 'ramda'
import { TYPE_GENERAL } from 'app/constants/messageType'
import { validationGeneral, validationBeacon } from './validate'
import { METERS_ID, RANGE_DEFAULT } from 'app/constants/beacon'
import { BEACON_DIALOG, TYPE_SEND_NOW } from 'app/constants/compose'
import { useIntl } from 'react-intl'
import { usePlace } from 'commons/usePlace'
import { isEmptyOrNil } from 'libs/empty'
import ComposeMessageDialog from './ComposeMessageDialog'
import useCreateNoti from 'app/main/composeMessage/useComposeSubmit'
import { useAIRequestId } from 'app/main/ai/usAIRequestId'
import { useFlag } from '@unleash/proxy-client-react'

const initialValues = (
  place,
  initialModel,
  isBeaconDialog,
  mes_4386_dashbooard_chats_coming_soon,
) => {
  const isFreeTier = place.activeSubscription.braintreePlan === PlanId.FREE_TIER

  const defaultTimezone =
    view(lensPath(['info', 'timezone']), place) || momentTimezone.tz.guess()

  // Unified timezone: use the first available timezone from any field, or default
  const timezoneCandidates = [
    initialModel?.scheduleTimezone,
    initialModel?.event?.startTimezone,
    initialModel?.reminderTimezone,
    initialModel?.poll?.endDateTimezone,
  ]
  const initialTimezone = first(compact(timezoneCandidates)) || defaultTimezone

  const initialValues = {
    topics: getOrDefault([])(() =>
      filter(propEq('isSelected', true))(initialModel.topics),
    ),
    recipients: getOrDefault({ subGroups: [], subscribers: [] })(
      () => initialModel.recipients,
    ),
    channels: getOrDefault({ channelIds: [], pageIds: [] })(() => {
      return {
        channelIds: initialModel.socialChannels.channels ?? [],
        pageIds: initialModel.socialChannels.pages.map(prop('id')) ?? [],
      }
    }),
    shortName: getOrDefault(undefined)(() => initialModel.shortName),
    title: getOrDefault(undefined)(() => initialModel.title),
    body: getOrDefault(undefined)(() => initialModel.body),
    location: {
      suggestion: getOrDefault(place.info.address)(
        () => initialModel.eventPlace.address,
      ),
      lng: getOrDefault(place.info.longitude)(
        () => initialModel.eventPlace.longitude,
      ),
      lat: getOrDefault(place.info.latitude)(
        () => initialModel.eventPlace.latitude,
      ),
      timeZoneId: getOrDefault(initialTimezone)(
        () => initialModel.eventPlace.timeZoneId,
      ),
    },
    allowConversation: getOrDefault(false)(() =>
      isFreeTier
        ? false
        : mes_4386_dashbooard_chats_coming_soon
          ? false
          : initialModel.allowConversation,
    ),
    isTtsEnabled: getOrDefault(true)(() =>
      isFreeTier ? false : initialModel.isTtsEnabled,
    ),
    isSharingEnabled: getOrDefault(isBeaconDialog ? true : false)(
      () => initialModel.isSharingEnabled,
    ),
    isDisplayHistory: getOrDefault(false)(() => initialModel.displayHistory),
    expirationTime: getOrDefault(0)(() => initialModel.expirationTime),
    notiType: getOrDefault(TYPE_GENERAL)(() => initialModel.notiType),
    priority: getOrDefault(1)(() => initialModel.priority),
    eventStartDateTime: getOrDefault(undefined)(
      () => initialModel.event.startDateTime,
    ),
    eventEndDateTime: getOrDefault(undefined)(
      () => initialModel.event.endDateTime,
    ),
    eventTimezone: initialTimezone,
    voucher: getOrDefault(undefined)(() => initialModel.voucher?.id),
    mediaSocials: getOrDefault([])(() => initialModel.mediaSocials),
    sendOptions: getOrDefault(TYPE_SEND_NOW)(() => initialModel.sendOptions),
    schedule: getOrDefault(undefined)(() => initialModel.scheduleDateTime),
    scheduleTimezone: initialTimezone,
    reminderChecked: getOrDefault(false)(
      () => !isNil(initialModel.reminderDateTime),
    ),
    reminder: getOrDefault(undefined)(() => initialModel.reminderDateTime),
    subscriberTotal: getOrDefault(undefined)(
      () => initialModel.subscriberTotal,
    ),
    reminderTimezone: initialTimezone,
    beacons: getOrDefault([])(() => initialModel.beacons),
    roomSize: '',
    proximityLength: getOrDefault(METERS_ID)(
      () => initialModel.proximityLength,
    ),
    range: getOrDefault(RANGE_DEFAULT)(() => initialModel.range),
    scheduleBeaconTimezone: getOrDefault(initialTimezone)(
      () => initialModel.beaconSchedule.start_timezone,
    ),
    addEndDateSchedule: getOrDefault(false)(() => {
      return (
        !isNil(initialModel.beaconSchedule.end_date) &&
        !isEmpty(initialModel.beaconSchedule.end_date)
      )
    }),
    scheduleStartDate: getOrDefault(undefined)(
      () => initialModel.beaconSchedule.start_date,
    ),
    scheduleEndDate: getOrDefault(undefined)(
      () => initialModel.beaconSchedule.end_date,
    ),
    frequencyType: getOrDefault('once')(() => {
      return initialModel.beaconSchedule.frequency_type === 'once'
        ? 'once'
        : 'repeat'
    }),

    frequencyUnit: getOrDefault('hour')(() => {
      return initialModel.beaconSchedule.frequency_type === 'once'
        ? 'hour'
        : initialModel.beaconSchedule.frequency_type
    }),
    duration: getOrDefault('')(() =>
      initialModel.beaconSchedule.frequency_type === 'once'
        ? ''
        : initialModel.beaconSchedule.duration,
    ),
    isBeaconEnabled: getOrDefault(true)(() => initialModel.isActive),
    question: getOrDefault('')(() => initialModel.question),
    options: getOrDefault([
      { title: '', imagePath: '' },
      { title: '', imagePath: '' },
    ])(() => {
      const pollOptions = initialModel.poll.options
      if (pollOptions && pollOptions.length > 0) {
        return [...pollOptions, { title: '', imagePath: '' }]
      }
      return pollOptions
    }),
    pollTimezone: initialTimezone,
    pollEndDateTime: getOrDefault(undefined)(() => initialModel.poll.endDate),
    additionalContext: getOrDefault('')(() => initialModel.additionalContext),
  }

  return initialValues
}

const ComposeMessageFormik = ({
  open,
  notification,
  onClose,
  onCreateSuccess,
  dialogType,
  isCopyMessage,
  valueFromAnotherPage,
}) => {
  const { formatMessage } = useIntl()
  const [validateOnChange, setValidateOnChange] = useState(true)

  const isDraft = propEq('isDraft', 1, notification)
  const isBeaconDialog = dialogType === BEACON_DIALOG
  const place = usePlace()
  const editId = prop('id', notification)

  const requestId = useAIRequestId(prop('requestId', notification))

  const isEditMode = compose(not, isEmptyOrNil)(editId) && !isDraft

  const handlerOnCreateSuccess = useCallback(
    (successModel, scope) => {
      if (!isNil(onCreateSuccess)) {
        onCreateSuccess(successModel, scope)
      }
      onClose()
    },
    [onClose, onCreateSuccess],
  )

  const { submitNoti, submitDraft, isSubmitting, submitErrorDetails } =
    useCreateNoti(
      handlerOnCreateSuccess,
      editId,
      isDraft,
      requestId,
      isCopyMessage,
    )

  const mes_4386_dashbooard_chats_coming_soon = useFlag(
    'mes_4386_dashbooard_chats_coming_soon',
  )

  const handleSubmitting = () => {
    if (!validateOnChange) {
      setValidateOnChange(true)
    }
  }

  return (
    <Formik
      initialValues={initialValues(
        place,
        notification,
        isBeaconDialog,
        mes_4386_dashbooard_chats_coming_soon,
      )}
      enableReinitialize={true}
      validateOnMount={isCopyMessage}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnChange}
      validationSchema={
        isBeaconDialog
          ? validationBeacon(notification, isEditMode, formatMessage, place)
          : validationGeneral(notification, isEditMode, formatMessage, place)
      }
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false)
        submitNoti({ ...values, isBeaconDialog })
      }}
    >
      {(formik) => {
        return (
          <Form>
            <ComposeMessageDialog
              formik={formik}
              notification={notification}
              open={open}
              onClose={onClose}
              isCopyMessage={isCopyMessage}
              isDraft={isDraft}
              dialogType={dialogType}
              submitNoti={submitNoti}
              submitDraft={submitDraft}
              isSubmitting={isSubmitting}
              submitErrorDetails={submitErrorDetails}
              onSubmitting={handleSubmitting}
              requestId={requestId}
              valueFromAnotherPage={valueFromAnotherPage}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default ComposeMessageFormik
